'use client';

import type { BoxProps } from '@mui/material/Box';
import type { Breakpoint } from '@mui/material/styles';
import type { ContainerProps } from '@mui/material/Container';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import { usePathname } from 'next/navigation';
import { isAdminPanelFullscreenPath } from '@/lib/adminRoutes';
import { useSettingsContext } from '../settings/context';
import { layoutClasses } from '../classes';

type MainProps = BoxProps & {
  isNavHorizontal: boolean;
};

/** Routes that should use full-bleed (no side padding / no Container max-width). */
const FULL_BLEED_PATHS = ['predictions/visualizer', 'journeys/details', 'journeys/create-new-journey', 'automation-flows/create', 'automation-flows/edit', 'email/templates/create-new-template', 'email/templates/edit', 'qr-code/details', 'ads/details', 'account/organization', 'ai-agent', 'arc', 'voice-workflows/create', 'conversations', 'email/inbox', 'creatives/build', 'voice-analytics'];


export function Main({ children, isNavHorizontal, sx, ...other }: MainProps) {
  const settings = useSettingsContext();
  const path = usePathname();
  const isFullBleed =
    (path && FULL_BLEED_PATHS.some((p) => path.includes(p))) || isAdminPanelFullscreenPath(path);

  return (
    <Box
      component="main"
      className={layoutClasses.main}
      sx={{
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minHeight: 0,
        overflow: isFullBleed ? 'hidden' : 'auto',
        ...(isNavHorizontal && {
          '--layout-dashboard-content-pt': '40px',
        }),
        ...sx,
      }}
      {...other}
    >
      {isFullBleed ? (
        <Box
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minWidth: 0,
            minHeight: 0,
            px: 0,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      ) : (
        <Container
          maxWidth={settings.compactLayout ? 'lg' : false}
          disableGutters
          sx={{
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            px: settings.compactLayout ? 2 : 0,
          }}
        >
          {children}
        </Container>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------------

type DashboardContentProps = ContainerProps & {
  disablePadding?: boolean;
};

export function DashboardContent({
  sx,
  children,
  disablePadding,
  maxWidth = 'lg',
  ...other
}: DashboardContentProps) {
  const theme = useTheme();

  const settings = useSettingsContext();

  const layoutQuery: Breakpoint = 'lg';

  return (
    <Container
      className={layoutClasses.content}
      maxWidth={settings.compactLayout ? maxWidth : false}
      disableGutters
      sx={{
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        pt: 'var(--layout-dashboard-content-pt)',
        pb: 'var(--layout-dashboard-content-pb)',
        [theme.breakpoints.up(layoutQuery)]: {
          px: 'var(--layout-dashboard-content-px)',
        },
        ...(disablePadding && {
          p: {
            xs: 0,
            sm: 0,
            md: 0,
            lg: 0,
            xl: 0,
          },
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Container>
  );
}
